import * as React from "react";
import { GridApi } from "ag-grid-community";
import { Export } from "./export";
import Button from "components/form/button";
import Checkbox from "@material-ui/core/Checkbox";
import { RadioButtonGroup, RadioButton } from "material-ui";
import { ListItem } from "material-ui/List";
import MenuItem from "material-ui/MenuItem";
import { formatMessage } from "lib/globalize-util";
import CircularProgress from "material-ui/CircularProgress";
import {
    IAllocationDimension,
    AllocationDimensionKind,
    AllocationDimensionIndex,
    getAllocationDimensionByIndex,
} from "helpers/allocation-dimension";
import Label from "components/form/label";
import Select from "components/form/select-field";
import CheckIcon from "@material-ui/icons/Check";
import { Form } from "formsy-react";
import { FormControlLabel } from "@material-ui/core";
import {
    ALLOCATION_DIMENSION_ID_PREFIX,
    ExportCloseoutUnitType,
    ExportCollectionSelectedSetting,
    ExportCollectionUnitType,
    ExportLayout,
    EXPORT_STATUS,
    IExportParams,
} from "constants/product-collection";
import { getAllocationDimensionsBySubtypeRaw } from "reducers/selectors/collection/collection-allocation-dimension";
import { DATATEST } from "constants/integration";
import exportStyles, { styles } from "./export-styles";
import me from "models/me";

const ListMenuItem: any = ListItem;
ListMenuItem.muiName = "MenuItem";

interface IProps {
    gridApi: GridApi;
    allocationDimensions?: IAllocationDimension[];
    collectionExport: any;
    onCloseExportDialog: () => void;
    onExport: (params: IExportParams) => void;
    isCloseoutAssortmentEnabled?: boolean;
}

export const ExportCollection: React.FC<IProps> = ({
    gridApi,
    allocationDimensions = [],
    collectionExport,
    onCloseExportDialog,
    onExport,
    isCloseoutAssortmentEnabled = false,
}) => {
    const [exportOnlyVisibleRows, setExportOnlyVisibleRows] = React.useState<ExportCollectionSelectedSetting>(
        ExportCollectionSelectedSetting.Selected
    );
    const [selectedCloseoutUnitType, setCloseoutUnitType] = React.useState<ExportCloseoutUnitType>(
        ExportCloseoutUnitType.PlannedUnits
    );
    const [exportOnlyVisibleColumns, setExportOnlyVisibleColumns] = React.useState<ExportCollectionSelectedSetting>(
        ExportCollectionSelectedSetting.Visible
    );
    const [withImages, setWithImages] = React.useState<boolean>(false);
    const [validationError, setValidationError] = React.useState<string>();
    const [selectedDimensionsId, setSelectedDimensionsId] = React.useState<string[]>([]);
    const [selectedColumnId, setSelectedColumnId] = React.useState<string>("");
    const [includeDoorNotes, setIncludeDoorNotes] = React.useState<boolean>(false);
    const [layoutOfSizes, setLayoutOfSizes] = React.useState<ExportLayout>(ExportLayout.Vertical);

    const sizeDimension = React.useMemo(() => {
        return allocationDimensions.find(
            (allocationDimension) => allocationDimension.subtype === AllocationDimensionKind.Size
        );
    }, [allocationDimensions]);

    const wasSizeDimensionSelected = React.useMemo(
        () => selectedDimensionsId.includes(sizeDimension?.id),
        [selectedDimensionsId, sizeDimension]
    );

    const getUnitType = () => {
        let unitType = ExportCollectionUnitType.Bulk;

        if (wasSizeDimensionSelected && selectedCloseoutUnitType === ExportCloseoutUnitType.PlannedUnits) {
            unitType = ExportCollectionUnitType.Actual;
        }

        if (selectedCloseoutUnitType === ExportCloseoutUnitType.AvailableUnits) {
            unitType = ExportCollectionUnitType.Inventory;
        }

        return unitType;
    };

    const collectionHasGrouping = () => {
        // @ts-ignore `columnModel` is private.
        const columnApi = gridApi.columnModel.columnApi;
        return (
            columnApi.isPivotMode() ||
            columnApi.getPivotColumns().length > 0 ||
            columnApi.getRowGroupColumns().length > 0 ||
            columnApi.getValueColumns().length > 0
        );
    };

    const validate = () => {
        let validationError;
        if (collectionHasGrouping()) {
            validationError = formatMessage(
                "Assortments cannot be exported with row groupings or while in Pivot mode. Please remove these settings and export again."
            );
        }

        if (validationError) {
            setValidationError(validationError);
        }
    };

    const handleExport = () => {
        if (validationError) {
            return;
        }

        const selectedAllocationDimensions = selectedDimensionsId.map((item) =>
            item.replace(ALLOCATION_DIMENSION_ID_PREFIX, "")
        );

        onExport({
            supplier: "",
            exportOnlyVisibleColumns,
            exportOnlyVisibleRows,
            withImages,
            withNotes: includeDoorNotes,
            withUnits:
                selectedDimensionsId.length > 0 || selectedCloseoutUnitType === ExportCloseoutUnitType.AvailableUnits,
            unitType: getUnitType(),
            layout: layoutOfSizes,
            selectedAllocationDimensions: selectedAllocationDimensions,
            horizontalAllocationDimensions: selectedColumnId.replace(ALLOCATION_DIMENSION_ID_PREFIX, ""),
        });
    };

    const handleChangeVisibleRows = React.useCallback((_event, value) => {
        setExportOnlyVisibleRows(value);
    }, []);

    const handleChangeVisibleColumns = React.useCallback((_event, value) => {
        setExportOnlyVisibleColumns(value);
    }, []);

    const handleChangeWithImages = React.useCallback((event, _value) => {
        setWithImages(event.target.checked);
    }, []);

    const handleChangeWithNotes = React.useCallback((event) => {
        setIncludeDoorNotes(event.target.checked);
    }, []);

    const handleChangeCloseoutUnitType = React.useCallback((event, value) => {
        setCloseoutUnitType(value);
    }, []);

    const handleDimensionsChange = (allocationDimension) => (event, _value) => {
        if (event.target.checked) {
            setSelectedDimensionsId([...selectedDimensionsId, allocationDimension.id]);
            if (allocationDimension.subtype === AllocationDimensionKind.Size) {
                setWithImages(false);
            }
        } else {
            selectedDimensionsId.splice(selectedDimensionsId.indexOf(allocationDimension.id), 1);
            setSelectedDimensionsId([...selectedDimensionsId]);
            if (allocationDimension.id === selectedColumnId) {
                setSelectedColumnId("");
            }
        }
    };

    const handleColumnChange = (_event, _index, selectedColumn) => {
        setSelectedColumnId(selectedColumn);
    };

    const handleChangeLayout = React.useCallback((_event, _index, value) => {
        setLayoutOfSizes(value);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const allocationDimensionsValue =
        allocationDimensions.length > 0 &&
        allocationDimensions.map((allocationDimension, i) => {
            return (
                <FormControlLabel
                    key={i}
                    control={
                        <Checkbox
                            checked={selectedDimensionsId.includes(allocationDimension.id)}
                            onChange={handleDimensionsChange(allocationDimension)}
                            name={allocationDimension.name}
                            className="checkbox"
                        />
                    }
                    label={allocationDimension.name}
                />
            );
        });
    const columnsValue =
        allocationDimensions.length > 0 &&
        allocationDimensions.map((allocationDimension, i) => {
            return (
                <MenuItem
                    key={i}
                    style={styles.subMenuItem}
                    value={allocationDimension.id}
                    disabled={!selectedDimensionsId.includes(allocationDimension.id)}
                    primaryText={allocationDimension.name}
                    rightIcon={
                        selectedColumnId === allocationDimension.id && (
                            <CheckIcon color={"secondary"} style={styles.selectedMenuItem} />
                        )
                    }
                />
            );
        });

    React.useEffect(() => {
        validate();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (gridApi.getSelectedRows().length === 0) {
            setExportOnlyVisibleRows(ExportCollectionSelectedSetting.Visible);
        }
    }, [gridApi, setExportOnlyVisibleRows]);

    const doorAllocationDimension = React.useMemo(() => {
        const genericAllocationDimensions = getAllocationDimensionsBySubtypeRaw(
            allocationDimensions,
            AllocationDimensionKind.Generic
        );
        return getAllocationDimensionByIndex(genericAllocationDimensions, AllocationDimensionIndex.Door);
    }, [allocationDimensions]);

    const shouldIncludeUnitsFor = React.useMemo(
        () =>
            (isCloseoutAssortmentEnabled && selectedCloseoutUnitType === ExportCloseoutUnitType.PlannedUnits) ||
            !isCloseoutAssortmentEnabled,
        [isCloseoutAssortmentEnabled, selectedCloseoutUnitType]
    );

    const shouldShowLayoutForSizes = React.useMemo(
        () => isCloseoutAssortmentEnabled && selectedCloseoutUnitType === ExportCloseoutUnitType.AvailableUnits,
        [isCloseoutAssortmentEnabled, selectedCloseoutUnitType]
    );

    return (
        <Export
            error={collectionExport.get("error") || collectionExport.get("status") === EXPORT_STATUS.FAILED}
            isLoading={!collectionExport.get("error") && collectionExport.get("status") === EXPORT_STATUS.CREATED}
            isReady={!collectionExport.get("error") && collectionExport.get("status") === EXPORT_STATUS.FINISHED}
        >
            <Export.Selection>
                <div {...exportStyles}>
                    <Form>
                        <div className="formGroupContainer">
                            <div>
                                <Label style={styles.label}>{formatMessage("Rows")}</Label>
                                <RadioButtonGroup
                                    name="rows"
                                    valueSelected={exportOnlyVisibleRows}
                                    onChange={handleChangeVisibleRows}
                                >
                                    <RadioButton
                                        disabled={gridApi.getSelectedRows().length === 0}
                                        label={formatMessage("Selected Rows Only")}
                                        value={ExportCollectionSelectedSetting.Selected}
                                    />
                                    <RadioButton
                                        label={formatMessage("Filtered Rows Only")}
                                        value={ExportCollectionSelectedSetting.Visible}
                                    />
                                    <RadioButton
                                        label={formatMessage("All Rows")}
                                        value={ExportCollectionSelectedSetting.All}
                                    />
                                </RadioButtonGroup>
                            </div>
                            <div>
                                <Label style={styles.label}>{formatMessage("Columns")}</Label>
                                <RadioButtonGroup
                                    name="columns"
                                    valueSelected={exportOnlyVisibleColumns}
                                    onChange={handleChangeVisibleColumns}
                                >
                                    <RadioButton
                                        label={formatMessage("Selected Columns Only")}
                                        value={ExportCollectionSelectedSetting.Visible}
                                    />
                                    <RadioButton
                                        label={formatMessage("All Columns")}
                                        value={ExportCollectionSelectedSetting.All}
                                    />
                                </RadioButtonGroup>
                            </div>
                        </div>
                        {isCloseoutAssortmentEnabled && !me.isBrandUser() && (
                            <div className="formGroupContainer closeoutUnitTypes">
                                <div>
                                    <Label style={styles.label}>{formatMessage("Type of units")}</Label>
                                    <RadioButtonGroup
                                        name="closeoutUnitTypes"
                                        valueSelected={selectedCloseoutUnitType}
                                        onChange={handleChangeCloseoutUnitType}
                                    >
                                        <RadioButton
                                            label={formatMessage("Planned Units")}
                                            value={ExportCloseoutUnitType.PlannedUnits}
                                        />
                                        <RadioButton
                                            label={formatMessage("Available Units (ATS)")}
                                            value={ExportCloseoutUnitType.AvailableUnits}
                                        />
                                    </RadioButtonGroup>
                                </div>
                            </div>
                        )}
                        {shouldIncludeUnitsFor && (
                            <div className="formGroupContainer">
                                <div>
                                    {allocationDimensions.length > 0 && (
                                        <Label style={styles.label}>{formatMessage("Include units for")}</Label>
                                    )}
                                    {allocationDimensionsValue &&
                                        allocationDimensionsValue.concat([
                                            <FormControlLabel
                                                key="include-door-notes"
                                                control={
                                                    <Checkbox
                                                        checked={includeDoorNotes}
                                                        onChange={handleChangeWithNotes}
                                                        className="checkbox"
                                                    />
                                                }
                                                label={formatMessage("Include Door Notes")}
                                                data-test={DATATEST.ASSORTMENTS.INCLUDE_DOOR_NOTES_CHK}
                                                disabled={
                                                    !selectedDimensionsId.includes(doorAllocationDimension?.id) ||
                                                    selectedColumnId !== doorAllocationDimension?.id
                                                }
                                            />,
                                        ])}
                                </div>
                                {allocationDimensions.length > 0 && (
                                    <div>
                                        <Label style={styles.label}>
                                            {formatMessage("Export horizontally as columns")}
                                        </Label>
                                        <Select
                                            disabled={selectedDimensionsId.length === 0}
                                            name="columns"
                                            value={selectedColumnId}
                                            onChange={handleColumnChange}
                                            style={styles.permissionSelector}
                                        >
                                            <MenuItem
                                                value={""}
                                                primaryText={formatMessage("None")}
                                                rightIcon={
                                                    !selectedColumnId && <CheckIcon style={styles.selectedMenuItem} />
                                                }
                                            />
                                            {columnsValue}
                                        </Select>
                                    </div>
                                )}
                            </div>
                        )}
                        {shouldShowLayoutForSizes && (
                            <div className="formGroupContainer layoutOfSizes">
                                <div>
                                    <Label style={styles.label}>{formatMessage("Layout of Sizes")}</Label>
                                    <Select
                                        name="layoutOfSizes"
                                        value={layoutOfSizes}
                                        onChange={handleChangeLayout}
                                        style={styles.permissionSelector}
                                    >
                                        <MenuItem
                                            value={ExportLayout.Vertical}
                                            primaryText={formatMessage("Vertical")}
                                        />
                                        <MenuItem
                                            value={ExportLayout.Horizontal}
                                            primaryText={formatMessage("Horizontal")}
                                        />
                                    </Select>
                                </div>
                            </div>
                        )}
                        <div className="bottomRow">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={withImages}
                                        disabled={wasSizeDimensionSelected}
                                        onChange={handleChangeWithImages}
                                        className="checkbox"
                                    />
                                }
                                label={formatMessage("Include images")}
                            />
                        </div>
                        <div className="buttonContainer">
                            <Button
                                label={formatMessage("Cancel")}
                                onClick={onCloseExportDialog}
                                style={styles.button.cancel}
                            />
                            <Button
                                label={formatMessage("Export")}
                                primary={true}
                                onClick={handleExport}
                                style={styles.button.export}
                                disabled={!!validationError}
                            />
                        </div>
                        {!!validationError && (
                            <div className="validationError">
                                <small>{validationError}</small>
                            </div>
                        )}
                    </Form>
                </div>
            </Export.Selection>
            <Export.Loading>
                <div {...exportStyles} className="loadingExport">
                    <CircularProgress />
                    <p>{formatMessage("Please wait as we prepare your assortment")}</p>
                </div>
            </Export.Loading>
            <Export.Ready>
                <a {...exportStyles} className="downloadExport" href={collectionExport.get("object_url")}>
                    <Button label={formatMessage("Download")} primary={true} large={true} />
                </a>
            </Export.Ready>
            <Export.Error>
                <p>{formatMessage("There was an error exporting your assortment.")}</p>
            </Export.Error>
        </Export>
    );
};
